import Button from '../button/button.js'
import './buttons-group.css'

const ELEMENT_NAME = 'wp-buttons-group'

/*
  Inspiration:
  https://www.radix-ui.com/primitives/docs/components/toggle-group#api-reference
*/

/**
 * A custom web component representing a Button with observable state.
 * pass a set of buttons: { label: 'Button 1', value: 'btn1' }, { label: 'Button 2', value: 'btn2' }
 * defaultValue: 'value' of the button to be active by default
 *
 * Init by
 * - Code
 * - HTML
 *
 *
 * Events:
 * wb:onSelectionChange: { detail: {
 *  value: <String||Array<String>>,
 *  selected: <Button||Array<Button>>
 *  component: <ButtonsGroup>
 * }}
 * wb:onInitialised: { detail: {
 *  value: <String||Array<String>>,
 *  selected: <Button||Array<Button>>
 *  component: <ButtonsGroup>
 * }}
 *
 * buttons gap: none (like Bootstrap btn-group) | gap
 * See also https://getbootstrap.com/docs/5.3/components/button-group/#button-toolbar
 *
 * .setValue -> set the correct button active based on value
 * .getValue -> get the value of the active button
 *
 * @param {Object} options
 * Properties:
 * @param {string} selectionType - single/multi (Default: single)
 * @param {Array<Button>} buttonsOptions
 *
 */
export default class ButtonsGroup extends HTMLElement {
  static observedAttributes = ['selection-type', 'data-size']
  static defaultTag = ELEMENT_NAME

  constructor (options) {
    super()
    console.log(`constructor for ${ELEMENT_NAME} start`)

    // Initialize component state
    this.$componentInfo = {
      // Component specific state
      elementId: `${ELEMENT_NAME}_` + Math.random().toString(36).substring(2, 9),
      clicks: options?.clicks || 0
    }

    this.$settings = {
      selectionType: options?.selectionType === 'multiple' ? 'multiple' : 'single',
      name: options?.name || ''
    }

    this.$data = {
      buttons: [],
      buttonsComponentIds: new Set(),
      selected: this.$settings.selectionType === 'multiple' ? [] : null,
      value: this.$settings.selectionType === 'multiple' ? [] : null
    }

    if (Array.isArray(options?.value)) {
      options.value.forEach(function (value) {
        addToSelection(this, value)
      }, this)
    } else if (options?.value) {
      console.log('addToSelection', options.value)
      addToSelection(this, options.value)
    }

    if (options?.buttonsOptions) {
      console.log('generate buttons')
      options?.buttonsOptions.forEach(function (buttonOptions) {
        const button = new Button(buttonOptions)
        this.appendChild(button)
        // this.$data.buttons.push(button)
      }, this)
    }

    console.log(`constructor done for ${ELEMENT_NAME} id=${this.$componentInfo.elementId}`, this)
  }

  get name () {
    return this.$settings.name
  }

  get selected () {
    return this.$data.selected
  }

  get value () {
    return this.$data.value
  }

  connectedCallback () {
    console.group('buttons-group connectedCallback')
    console.log(`connectedCallback() for ${ELEMENT_NAME} id=${this.$componentInfo.elementId}`)
    // this.render()

    console.log('ButtonsGroup connectedCallback', this)

    // Init observer to identify children buttons
    observeChildren(this)

    refreshButtonsStates(this)
    console.groupEnd()
  }

  disconnectedCallback () {
    // Remove listener when Button is removed
    console.log(`Custom element removed from page: ${ELEMENT_NAME} id=${this.$componentInfo.elementId}`)
  }

  attributeChangedCallback (name, oldValue, newValue) {
    console.group('attributeChangedCallback')
    console.log(`Attribute ${name} has changed from [${oldValue}] to [${newValue}]`)
    this.$data[name] = newValue

    if (oldValue === newValue) return

    if (name === 'selection-type') {
      this.$settings.selectionType = newValue === 'multiple' ? 'multiple' : 'single'
      if (this.$settings.selectionType === 'multiple') {
        this.$data.selected = []
        this.$data.value = []
      } else {
        this.$data.selected = null
        this.$data.value = null
      }
    }
    console.log('after attributeChangedCallback', this.$settings)
    console.groupEnd()
  }
}

if (!customElements.get(ELEMENT_NAME)) {
  customElements.define(ELEMENT_NAME, ButtonsGroup)
}

function addToSelection (instance, value) {
  if (instance.$settings.selectionType === 'single') {
    instance.$data.value = value
  } else {
    instance.$data.value.push(value)
  }
  updateSelection(instance)
}

function removeFromSelection (instance, value) {
  if (instance.$settings.selectionType === 'single' && instance.$data.value === value) {
    instance.$data.value = null
  } else if (instance.$settings.selectionType === 'multiple') {
    instance.$data.value = instance.$data.value.filter(v => v !== value)
  }
  updateSelection(instance)
}

function updateSelection (instance) {
  if (instance.$settings.selectionType === 'single') {
    instance.$data.selected = instance.$data.buttons.find(b => b.value === instance.$data.value)
  } else {
    instance.$data.selected = instance.$data.buttons.filter(b => instance.$data.value.includes(b.value))
  }
}

function refreshButtonsStates (instance) {
  console.group('refreshButtonsStates')
  console.log('refreshButtonsStates', instance.$data.buttons)
  instance.$data.buttons.forEach(function (c) {
    // console.log(`Update button ${c.$componentInfo.elementId} value=${c.value}`)
    const shouldBeActive = instance.$data.value === c.value || instance.$data.value?.includes(c.value)
    // console.log('shouldBeActive=', shouldBeActive)
    c.active = shouldBeActive
    // buttonInstance.setAttribute('active', shouldBeActive)
  })
  console.log('refreshButtonsStates done', instance.$data.buttons)
  console.groupEnd()
}

function changeInChildButton (instance, buttonInstance) {
  console.group('changeInChildButton')
  console.log(`Button ${buttonInstance.$componentInfo.elementId} active= ${buttonInstance.active} value=${buttonInstance.value}`, buttonInstance.$state)

  // console.log('instance', instance)
  // console.log('selectionType', instance.$settings.selectionType)

  if (buttonInstance.active) {
    addToSelection(instance, buttonInstance.value)
  } else {
    removeFromSelection(instance, buttonInstance.value)
  }

  refreshButtonsStates(instance)

  instance.dispatchEvent(new CustomEvent('wb:onSelectionChange', {
    detail: {
      component: instance,
      selected: instance.$data.selected,
      value: instance.$data.value
    },
    bubbles: true,
    composed: true
  }))
  console.groupEnd()
}

function observeChildren (instance) {
  const observer = new MutationObserver(mutationsList => {
    // console.group('MutationObserver')
    for (const mutation of mutationsList) {
      // console.log('observeChildren', (mutation.target instanceof Button), mutation)
      if (!(mutation.target instanceof Button)) return

      // Only register the listener once
      const targetButton = mutation.target
      const buttonsComponentIds = instance.$data.buttonsComponentIds
      // console.log('observeChildren add listener', targetButton.$componentInfo.elementId, 'active?', targetButton.active)
      if (buttonsComponentIds.has(targetButton.$componentInfo.elementId)) return

      buttonsComponentIds.add(targetButton.$componentInfo.elementId)

      instance.$data.buttons.push(mutation.target)

      if (targetButton.active) addToSelection(instance, targetButton.value)

      mutation.target.addEventListener('wp:onClick', function (event) {
        // console.log('group wp:onClick', event)

        if (event.constructor.name !== 'CustomEvent') return
        const customEvent = /** @type {CustomEvent} */ (event)

        const targetComponent = customEvent.detail.component

        changeInChildButton(instance, targetComponent)
      })
    }
    // console.groupEnd()
    // console.log('MutationObserver done')
    // console.log('group values', instance.$data.selected, instance.$data.value)
    instance.dispatchEvent(new CustomEvent('wb:initialised', {
      detail: {
        component: instance,
        selected: instance.$data.selected,
        value: instance.$data.value
      }
    }))
  })

  // Observe the current element or its shadowRoot for childList changes
  observer.observe(instance, { childList: true, subtree: true })
}
